import { Button, Card, Muted, Stack, color, space, status } from '@biopeak/ui';
import {
  AnalysisError,
  useGenerateAnalysis,
  useLatestAnalysis,
  type AnalysisContent,
  type StoredAnalysis,
} from '../lib/analysis.ts';

/**
 * Análise do paciente por IA.
 *
 * No desktop, ocupa a largura total da grade de 3 colunas via a classe
 * `doctor-overview-card--wide` (`grid-column: span 3` em `global.css`). No
 * celular a grade vira carrossel (`.doctor-overview-grid`) e este card é só
 * mais um slide, largura cheia igual aos outros — a classe não faz nada
 * abaixo de 860px, ver a media query em `global.css`.
 *
 * DUAS REGRAS DE INTERFACE QUE NÃO SÃO NEGOCIÁVEIS AQUI:
 *
 * 1. Nunca exibir a análise sem dizer que ela foi gerada por IA, sobre quantos
 *    dias, e quando. Um texto clínico sem procedência é indistinguível de
 *    laudo, e não é isso que ele é.
 * 2. Achado não verificado aparece VISUALMENTE distinto. A promessa da feature
 *    é que todo número foi calculado pelo Postgres; quando o modelo cita um
 *    número que não estava no brief, esconder isso transformaria a promessa em
 *    mentira. Ver `unverified_claims` na edge function.
 */
export function PatientAnalysisCard({ patientId }: { patientId: string }) {
  const guardada = useLatestAnalysis(patientId);
  const gerar = useGenerateAnalysis(patientId);

  const analise = guardada.data;
  const erro = gerar.error instanceof AnalysisError ? gerar.error : null;
  const semDado = gerar.data?.status === 'sem_dado';

  return (
    <div className="doctor-overview-card--wide">
    <Card>
      <Stack gap={space.md}>
        <div
          style={{
            display: 'flex',
            alignItems: 'baseline',
            justifyContent: 'space-between',
            gap: space.sm,
            flexWrap: 'wrap',
          }}
        >
          <span style={{ fontSize: '17px', fontWeight: 600 }}>Análise do período</span>
          <Button
            variant="secondary"
            onClick={() => gerar.mutate({ force: Boolean(analise) })}
            disabled={gerar.isPending}
          >
            {gerar.isPending
              ? 'Analisando…'
              : analise
                ? 'Analisar de novo'
                : 'Analisar'}
          </Button>
        </div>

        {erro && <Erro erro={erro} />}

        {semDado && (
          <Muted style={{ fontSize: '14px' }}>
            Ainda não há dado suficiente deste paciente para uma análise.
          </Muted>
        )}

        {!analise && !erro && !semDado && !gerar.isPending && (
          <Muted style={{ fontSize: '14px' }}>
            Nenhuma análise gerada ainda. O texto cruza todas as métricas do período,
            aponta o que mudou e sugere perguntas para a consulta — nunca diagnóstico.
          </Muted>
        )}

        {analise && <Conteudo registro={analise} />}
      </Stack>
    </Card>
    </div>
  );
}

/**
 * Cada código de erro pede uma ação diferente de quem está lendo.
 *
 * "Não autorizado pelo paciente" é a mais importante de não confundir com falha
 * técnica: não é bug, é o consentimento funcionando, e a saída é conversar com
 * o paciente — não abrir chamado.
 */
function Erro({ erro }: { erro: AnalysisError }) {
  const tom = erro.code === 'sem_consentimento' ? status.yellow : status.red;

  return (
    <div
      style={{
        background: tom.bg,
        border: `1px solid ${tom.border}`,
        borderRadius: '8px',
        padding: space.sm,
      }}
    >
      <span style={{ fontSize: '14px', color: tom.fg }}>
        {erro.code === 'sem_consentimento' && (
          <>
            <strong>O paciente ainda não autorizou a análise por IA.</strong> A
            autorização é dada por ele, no aplicativo, e pode ser revogada quando
            quiser.
          </>
        )}
        {erro.code === 'sem_chave' && (
          <>
            <strong>O serviço de análise não está configurado.</strong> Falta a chave
            da OpenAI nas variáveis da edge function.
          </>
        )}
        {erro.code !== 'sem_consentimento' && erro.code !== 'sem_chave' && erro.message}
      </span>
    </div>
  );
}

interface Direcao {
  rotulo: string;
  cor: string;
}

/**
 * `estavel` é o fallback: o enum do schema garante um dos quatro, mas se um
 * valor novo aparecer o card mostra "estável" em vez de quebrar a página do
 * médico. Degradar é melhor que sumir.
 */
const DIRECAO_PADRAO: Direcao = { rotulo: '→ estável', cor: color.textSecondary };

const DIRECAO: Record<string, Direcao> = {
  melhora: { rotulo: '↑ melhora', cor: status.green.fg },
  piora: { rotulo: '↓ piora', cor: status.red.fg },
  estavel: { rotulo: '→ estável', cor: color.textSecondary },
  instavel: { rotulo: '↕ instável', cor: status.yellow.fg },
};

function Conteudo({ registro }: { registro: StoredAnalysis }) {
  const a = registro.analysis as unknown as AnalysisContent;
  const naoVerificados = Array.isArray(registro.unverified_claims)
    ? registro.unverified_claims.length
    : 0;

  return (
    <Stack gap={space.md}>
      <p style={{ margin: 0, fontSize: '15px', lineHeight: 1.6 }}>{a.resumo}</p>

      {a.achados?.length > 0 && (
        <Stack gap={space.sm}>
          {a.achados.map((achado, i) => {
            const dir = DIRECAO[achado.direcao] ?? DIRECAO_PADRAO;
            // Não verificado ganha borda de aviso. É o único caso em que a cor
            // fala de confiabilidade do TEXTO, não do estado clínico.
            const suspeito = achado.verificado === false;
            return (
              <div
                key={i}
                style={{
                  borderLeft: `3px solid ${suspeito ? status.yellow.border : color.border}`,
                  background: suspeito ? status.yellow.bg : 'transparent',
                  paddingLeft: space.sm,
                  paddingTop: '2px',
                  paddingBottom: '2px',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'baseline',
                    gap: space.sm,
                    flexWrap: 'wrap',
                  }}
                >
                  <span style={{ fontSize: '15px', fontWeight: 600 }}>{achado.titulo}</span>
                  <span style={{ fontSize: '13px', color: dir.cor }}>{dir.rotulo}</span>
                  <span style={{ fontSize: '12px', color: color.textSecondary }}>
                    confiança {achado.confianca}
                  </span>
                </div>
                <p
                  style={{
                    margin: `2px 0 0`,
                    fontSize: '14px',
                    lineHeight: 1.5,
                    color: color.text,
                  }}
                >
                  {achado.observacao}
                </p>
                {suspeito && (
                  <p style={{ margin: '4px 0 0', fontSize: '12px', color: status.yellow.fg }}>
                    Este achado cita número que não foi encontrado no cálculo. Confira
                    nos gráficos antes de considerá-lo.
                  </p>
                )}
              </div>
            );
          })}
        </Stack>
      )}

      {a.atencao?.length > 0 && (
        <Stack gap={space.xs}>
          <span style={{ fontSize: '14px', fontWeight: 600 }}>Merece atenção</span>
          {a.atencao.map((item, i) => (
            <div key={i} style={{ fontSize: '14px', lineHeight: 1.5 }}>
              <strong>{item.titulo}.</strong> {item.porque}
            </div>
          ))}
        </Stack>
      )}

      {a.perguntas_sugeridas?.length > 0 && (
        <Stack gap={space.xs}>
          <span style={{ fontSize: '14px', fontWeight: 600 }}>
            Perguntas para a consulta
          </span>
          <ul style={{ margin: 0, paddingLeft: '1.1rem' }}>
            {a.perguntas_sugeridas.map((p, i) => (
              <li key={i} style={{ fontSize: '14px', lineHeight: 1.5 }}>
                {p}
              </li>
            ))}
          </ul>
        </Stack>
      )}

      <Muted style={{ fontSize: '12px' }}>{a.qualidade_do_dado}</Muted>

      {/* Procedência. Sem isto o texto vira laudo anônimo — e a data importa
          tanto quanto a origem: uma análise de duas semanas atrás descreve um
          paciente que pode não existir mais. */}
      <Muted style={{ fontSize: '12px', borderTop: `1px solid ${color.border}`, paddingTop: space.xs }}>
        Gerado por inteligência artificial sobre {registro.window_days} dias de dados,
        em {new Date(registro.generated_at).toLocaleString('pt-BR')}. Todos os números
        são calculados pelo SerBio Health, não pelo modelo
        {naoVerificados > 0 && (
          <> — exceto {naoVerificados} achado(s) marcado(s) acima</>
        )}
        . Não substitui avaliação clínica.
      </Muted>
    </Stack>
  );
}
